/**
 * DocumentSearch — /documents/search (eDMS E6: full-text + faceted search).
 *
 * Full-text search across the document register (number + title) with faceted
 * filters (status / type / classification). Runs client-side over the live
 * `/documents` list — fast for the register size, and the facet counts update
 * live as the query narrows. A production build would push the query + facets
 * to a server-side index (pg_trgm / OpenSearch).
 */
import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { PageHero, StatCard, Stagger, Skeleton, EmptyState, Button } from '@edms/design-system';
import { apiFetch, downloadTextFile } from '../../api/client';
import styles from './DocumentSearch.module.css';

interface DocRow {
  document_id: string;
  doc_number: string;
  title: string;
  status: string;
  document_type: string;
  classification: string;
  updated_at_utc?: string;
}

type FacetKey = 'status' | 'document_type' | 'classification';
const FACETS: { key: FacetKey; label: string }[] = [
  { key: 'status', label: 'Status' },
  { key: 'document_type', label: 'Type' },
  { key: 'classification', label: 'Classification' },
];

const PAGE = 200; // render window — the register now holds ~1.8k docs; render in
                  // pages so we never mount thousands of DOM rows at once. Search +
                  // facets still run over the full set; only the LIST is windowed.

export function DocumentSearch() {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [shown, setShown] = useState(PAGE);
  const [sel, setSel] = useState<Record<FacetKey, Set<string>>>({
    status: new Set(),
    document_type: new Set(),
    classification: new Set(),
  });

  const docsQ = useQuery<{ items: DocRow[] }>({
    queryKey: ['documents', 'search-all'],
    queryFn: ({ signal }) => apiFetch<{ items: DocRow[] }>('/api/v1/documents', { signal }),
    staleTime: 60_000,
  });
  const all = useMemo(() => docsQ.data?.items ?? [], [docsQ.data]);

  const query = q.trim().toLowerCase();
  const textMatch = (d: DocRow) =>
    !query ||
    (d.doc_number ?? '').toLowerCase().includes(query) ||
    (d.title ?? '').toLowerCase().includes(query);

  // A doc passes a facet group if nothing's selected there, or its value is in the set.
  const facetMatch = (d: DocRow, except?: FacetKey) =>
    FACETS.every(({ key }) => {
      if (key === except) return true;
      const s = sel[key];
      return s.size === 0 || s.has(String(d[key] ?? ''));
    });

  const results = useMemo(
    () => all.filter((d) => textMatch(d) && facetMatch(d)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, query, sel],
  );

  // Facet counts respect the text query + the OTHER active facets (faceted-search semantics).
  const facetValues = useMemo(() => {
    const out: Record<FacetKey, { value: string; count: number }[]> = {
      status: [], document_type: [], classification: [],
    };
    for (const { key } of FACETS) {
      const counts = new Map<string, number>();
      for (const d of all) {
        if (!textMatch(d) || !facetMatch(d, key)) continue;
        const v = String(d[key] ?? '—');
        counts.set(v, (counts.get(v) ?? 0) + 1);
      }
      out[key] = [...counts.entries()].map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, query, sel]);

  function toggle(key: FacetKey, value: string) {
    setShown(PAGE);
    setSel((prev) => {
      const next: Record<FacetKey, Set<string>> = {
        status: new Set(prev.status), document_type: new Set(prev.document_type), classification: new Set(prev.classification),
      };
      if (next[key].has(value)) next[key].delete(value);
      else next[key].add(value);
      return next;
    });
  }
  const activeFacetCount = sel.status.size + sel.document_type.size + sel.classification.size;
  function clearAll() {
    setQ('');
    setShown(PAGE);
    setSel({ status: new Set(), document_type: new Set(), classification: new Set() });
  }

  // B-1 — Master Document Register export (audited, full GMP columns). Sends the
  // text query + any single-selected facet so the CSV matches the on-screen view.
  const [exporting, setExporting] = useState(false);
  async function exportRegister() {
    setExporting(true);
    try {
      const one = (k: FacetKey) => (sel[k].size === 1 ? [...sel[k]][0] : undefined);
      const res = await apiFetch<{ csv: string; filename: string; row_count: number }>(
        '/api/v1/documents/register/export',
        {
          method: 'POST',
          body: {
            q: q.trim() || undefined,
            status: one('status'),
            document_type: one('document_type'),
            classification: one('classification'),
          },
        },
      );
      downloadTextFile(res.csv, res.filename || 'master-document-register.csv');
    } catch {
      /* transient; the button re-enables */
    } finally {
      setExporting(false);
    }
  }

  // Highlight the matched substring in a label.
  function hl(text: string) {
    if (!query) return text;
    const i = text.toLowerCase().indexOf(query);
    if (i < 0) return text;
    return (
      <>
        {text.slice(0, i)}
        <mark className={styles.mark}>{text.slice(i, i + query.length)}</mark>
        {text.slice(i + query.length)}
      </>
    );
  }

  return (
    <div className={styles.screen}>
      <PageHero
        eyebrow={t('docSearch.eyebrow', { defaultValue: 'Documents' })}
        title={t('docSearch.title', { defaultValue: 'Search the document register' })}
        subtitle={t('docSearch.subtitle', { defaultValue: 'Full-text search across every controlled document, with live facets for status, type and classification.' })}
        actions={
          <Button variant="primary" onClick={() => void exportRegister()} isLoading={exporting} disabled={!docsQ.data}>
            {t('docSearch.export', { defaultValue: 'Export to Excel' })}
          </Button>
        }
      />

      <div className={styles.searchRow}>
        <input
          type="search"
          className={styles.searchInput}
          placeholder={t('docSearch.placeholder', { defaultValue: 'Search by document number or title…' })}
          value={q}
          onChange={(e) => { setQ(e.target.value); setShown(PAGE); }}
          aria-label={t('docSearch.title', { defaultValue: 'Search the document register' })}
          autoFocus
        />
        {(activeFacetCount > 0 || q) && (
          <button type="button" className={styles.clearBtn} onClick={clearAll}>
            {t('docSearch.clear', { defaultValue: 'Clear ({{n}})', n: activeFacetCount + (q ? 1 : 0) })}
          </button>
        )}
      </div>

      {docsQ.isLoading && (
        <div className={styles.layout}>
          <Skeleton height={300} radius={14} />
          <div>{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} height={56} radius={10} />)}</div>
        </div>
      )}

      {docsQ.isError && (
        <p className={styles.error} role="alert">{t('docSearch.error', { defaultValue: 'Could not load the document register.' })}</p>
      )}

      {docsQ.data && (
        <>
          <Stagger className={styles.kpiStrip} step={50}>
            <StatCard label={t('docSearch.kpi.total', { defaultValue: 'Documents' })} value={all.length} accent="var(--spectro-purple, #9333EA)" />
            <StatCard label={t('docSearch.kpi.matches', { defaultValue: 'Matches' })} value={results.length} accent="var(--spectro-blue, #0065FF)" deltaCaption={query ? `“${q}”` : t('docSearch.kpi.allShown', { defaultValue: 'all shown' })} />
            <StatCard label={t('docSearch.kpi.facets', { defaultValue: 'Active facets' })} value={activeFacetCount} accent="var(--spectro-violet, #3F3DFF)" />
          </Stagger>

          <div className={styles.layout}>
            <aside className={styles.facets} aria-label={t('docSearch.facetsAria', { defaultValue: 'Filters' })}>
              {FACETS.map(({ key, label }) => (
                <section key={key} className={styles.facetGroup}>
                  <h2 className={styles.facetTitle}>{label}</h2>
                  <ul className={styles.facetList}>
                    {facetValues[key].map(({ value, count }) => (
                      <li key={value}>
                        <label className={styles.facetItem}>
                          <input
                            type="checkbox"
                            checked={sel[key].has(value)}
                            onChange={() => toggle(key, value)}
                          />
                          <span className={styles.facetValue}>{value}</span>
                          <span className={styles.facetCount}>{count}</span>
                        </label>
                      </li>
                    ))}
                    {facetValues[key].length === 0 && <li className={styles.facetEmpty}>—</li>}
                  </ul>
                </section>
              ))}
            </aside>

            <div className={styles.results}>
              {results.length === 0 ? (
                <EmptyState
                  icon="🔍"
                  title={t('docSearch.emptyTitle', { defaultValue: 'No documents match' })}
                  description={t('docSearch.empty', { defaultValue: 'Try a different search term, or clear some facets.' })}
                />
              ) : (
                <>
                  <ul className={styles.resultList}>
                    {results.slice(0, shown).map((d) => (
                      <li key={d.document_id} className={styles.resultRow}>
                        <Link to="/documents/$id" params={{ id: d.document_id }} className={styles.resultLink}>
                          <span className={styles.resultNumber}>{hl(d.doc_number)}</span>
                          <span className={styles.resultTitle}>{hl(d.title)}</span>
                          <span className={styles.resultMeta}>
                            <span className={styles.badge}>{d.document_type}</span>
                            <span className={styles.badge}>{d.classification}</span>
                            <span className={`${styles.badge} ${styles.badgeStatus}`}>{d.status}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  {results.length > shown && (
                    <div className={styles.loadMore}>
                      <span className={styles.loadMoreCount}>
                        {t('docSearch.showing', { defaultValue: 'Showing {{shown}} of {{total}}', shown, total: results.length })}
                      </span>
                      <Button variant="secondary" onClick={() => setShown((n) => n + PAGE)}>
                        {t('docSearch.loadMore', { defaultValue: 'Load more' })}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
